// Locale → copy for the public "The new Photohawk web uploader" page, following the site's language like
// the pricing and preview-first pages: English (source of truth), Spanish,
// Portuguese, German, French, Italian, Dutch, Norwegian (Bokmål), Japanese, Simplified Chinese, Malay and Thai
// (SEO_LANGS in src/lib/seo/site.ts); any other language falls back to English. A ?lang= the site UI doesn't
// have (fr, nl, no, th) still renders this copy; only the nav and footer stay in English (i18next fallbackLng).
import { useTranslation } from "react-i18next";
import { getApiBaseDomain } from "@/lib/apiDomain";
import { baseLang } from "@/lib/seo/site";
import en, { type WebUploaderCopy } from "./copy.en";
import es from "./copy.es";
import pt from "./copy.pt";
import de from "./copy.de";
import fr from "./copy.fr";
import it from "./copy.it";
import nl from "./copy.nl";
import no from "./copy.no";
import ja from "./copy.ja";
import zh from "./copy.zh";
import ms from "./copy.ms";
import th from "./copy.th";

/** Where the page lives on the marketing site (linked from the web uploader and the launch email). */
export const WEB_UPLOADER_PATH = "/web-uploader";

/** The page's absolute URL for the current environment (photohawk.com or photohawk-staging.com), for links
 *  from the uploader, which also runs on upload.<domain> and on tenant hosts. */
export const webUploaderPageUrl = () => `https://${getApiBaseDomain()}${WEB_UPLOADER_PATH}`;

/** The web uploader itself (sign-in first if needed). */
export const UPLOADER_URL = "https://upload.photohawk.com";

/** "How on-demand FTP works" (/on-demand-ftp, the same page the FTP server panel links to). */
export { ON_DEMAND_FTP_PATH as ON_DEMAND_FTP_HELP_URL } from "@/content/onDemandFtp";

const COPY_BY_LANG: Record<string, WebUploaderCopy> = { en, es, pt, de, fr, it, nl, no, ja, zh, ms, th };

export const getWebUploaderCopy = (lang: string | undefined): WebUploaderCopy => COPY_BY_LANG[baseLang(lang)] ?? en;

export function useWebUploaderCopy(): { copy: WebUploaderCopy; lang: string } {
  const { i18n } = useTranslation();
  const base = baseLang(i18n.language);
  const lang = COPY_BY_LANG[base] ? base : "en";
  return { copy: COPY_BY_LANG[lang], lang };
}

export type { WebUploaderCopy };
